import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useGetAccess } from './backend/interviews'
import { nextDegradedDelay } from './degradedRetry'

export type AccessRole = 'admin' | 'viewer' | 'manager'
export type AccessStatus = 'ok' | 'degraded'

export type Access = {
  role: AccessRole
  is_admin: boolean
  is_viewer: boolean
  can_act: boolean
  sees_all: boolean
  manager_id: string | null
  caption: string
  status: AccessStatus
  linked: boolean
}

type AccessContextValue = {
  access: Access | null
  loading: boolean
  canAct: boolean
  isViewer: boolean
  seesAll: boolean
  caption: string
  degraded: boolean
  linked: boolean
  retry: () => void
}

const DEFAULT: AccessContextValue = {
  access: null,
  loading: true,
  canAct: false,
  isViewer: false,
  seesAll: false,
  caption: '',
  degraded: false,
  linked: false,
  retry: () => {},
}

const AccessContext = createContext<AccessContextValue>(DEFAULT)

export function AccessProvider({ children }: { children: ReactNode }) {
  const { data, loading, trigger } = useGetAccess()

  useEffect(() => {
    trigger({})
  }, [trigger])

  // A DEGRADED REPLY MUST NEVER OVERWRITE A GOOD ONE.
  //
  // useCapabilities learned this on 26 August 2026 and this file did not, which
  // is why on 30 August the sidebar read "Login not linked to a manager" under
  // an admin account whose login IS linked: resolveAccess answers all-false with
  // status 'degraded' when its query fails, `linked` is one of those falses, and
  // nothing here was reading the status. One dropped connection therefore told
  // Luyanda his own login was not linked to a profile.
  //
  // Same remedy as useCapabilities. The last answer that actually resolved stays
  // in force, and a degraded reply raises the banner without taking anything
  // away. Only a degraded reply with no good answer behind it is allowed to
  // stand on its own, which is the genuine "we have never been able to check"
  // case.
  const lastGood = useRef<Access | null>(null)

  const value = useMemo<AccessContextValue>(() => {
    const incoming = (data as Access | null | undefined) ?? null
    if (incoming !== null && incoming.status !== 'degraded') {
      lastGood.current = incoming
    }
    const isDegraded = incoming?.status === 'degraded'
    const access = isDegraded ? (lastGood.current ?? incoming) : incoming
    return {
      access,
      // Same rule as useCapabilities: loading means "not known yet", never
      // "being re-checked". The interview pages gate on this the same way, so
      // without it a write on Data Fixes blanks the page it was made from.
      loading: loading && access === null && lastGood.current === null,
      // Until access resolves, default to no-action so buttons never flash for viewers.
      canAct: access?.can_act === true,
      isViewer: access?.is_viewer === true,
      seesAll: access?.sees_all === true,
      caption: access?.caption ?? '',
      // The flags come from the last good answer; the banner comes from the reply
      // in hand. Both are true at once, and the screen shows the banner over real
      // data instead of a false "not linked" over none.
      degraded: isDegraded,
      linked: access?.linked === true,
      retry: () => trigger({}),
    }
  }, [data, loading, trigger])

  // Bounded automatic re-check, same reasoning and same schedule as
  // useCapabilities. See degradedRetry.ts.
  const degradedAttempts = useRef(0)
  useEffect(() => {
    if (!value.degraded) {
      degradedAttempts.current = 0
      return
    }
    const delay = nextDegradedDelay(degradedAttempts.current)
    if (delay === null) return
    const timer = setTimeout(() => {
      degradedAttempts.current += 1
      trigger({})
    }, delay)
    return () => clearTimeout(timer)
  }, [value.degraded, data, trigger])

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
}

export function useAccess() {
  return useContext(AccessContext)
}
